// Shared, published-fidelity display markup for rich-text leaves. The PUBLIC
// PageRenderer and the builder's EditableCanvas (for any field that isn't being
// actively edited) both render through these, so the editor's WYSIWYG is
// guaranteed pixel-identical to the live site — there's exactly one place that
// turns a text/hero block into HTML, and no second copy to drift.
//
// Rich text is authored as HTML in the native editor, but the display side is editor-free:
// prepareRichTextHtml inlines the list/link styles a stylesheet would
// otherwise provide, so the static render needs none and matches the editor.
import React, { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { PageTheme, HeroBlock, LibraryBlock, MiniNavBlock } from '../../../shared/pageBlocks';
import type { TextBlock } from '../../../shared/newsletterBlocks';
import { textContentStyle, prepareRichTextHtml, fontStack } from '../../../shared/newsletterBlocks';
import { themeHeadingSizes } from '../../../shared/pageBlocks';
import { asNewsletterTheme } from './leafWebStyles';
import { getPreviewKind } from '@/utils/fileUtils';
import { LibraryMediaView } from './components/LibraryMediaView';
import { LibraryFolderBrowser } from './components/LibraryFolderBrowser';

// Published hero type scale. Exported so the builder's EDIT surfaces (the native editor
// contentStyle, CTA wrapper) reuse the exact same numbers as the static render.
// Heading/subheading deliberately carry NO color — they inherit hero.textColor
// from heroContentStyle, exactly like the public page.
export const heroHeadingStyle = (theme: PageTheme): CSSProperties =>
  ({ margin: 0, fontSize: 'clamp(32px, 6vw, 64px)', fontWeight: 700, fontFamily: fontStack(theme.fontPackId) });
export const heroSubStyle: CSSProperties =
  { margin: '16px 0 0', fontSize: 'clamp(16px, 2.5vw, 22px)', opacity: 0.9 };
export const heroCtaStyle = (theme: PageTheme): CSSProperties =>
  ({ display: 'inline-block', marginTop: 28, padding: '14px 32px', backgroundColor: theme.accentColor, color: '#ffffff', borderRadius: 8, fontWeight: 700, textDecoration: 'none' });

// Resolve a block link target: external URL → new-tab <a>; legacy absolute path
// (/g/...) → <a href>; otherwise a RELATIVE page slug / product handle → SPA
// navigate via onNavigate (which builds /g/:orgSlug/<target> from the live
// URL). Shared by every leaf that links out (hero CTA, image/button href,
// mini-nav links) so target resolution never drifts between them.
export const SmartLink: React.FC<{ target: string; onNavigate?: (t: string) => void; style?: CSSProperties; children: React.ReactNode }> = ({ target, onNavigate, style, children }) => {
  if (!target) return <>{children}</>;
  if (/^https?:\/\//i.test(target)) return <a href={target} target="_blank" rel="noreferrer" style={style}>{children}</a>;
  if (target.startsWith('/')) return <a href={target} style={style}>{children}</a>;
  return <a href={`#${target}`} onClick={(e) => { e.preventDefault(); onNavigate?.(target); }} style={style}>{children}</a>;
};

/** The inner content of a text block (the styled div + rich HTML). Callers wrap
 *  it in `<div style={textBlockStyle(block)}>` (align/padding/background).
 *  Heading tags get the theme's H1/H2/H3 sizes inlined so the published page
 *  matches the editor (which reads the same sizes via headingSizesOf). */
export const TextLeafContent: React.FC<{ block: TextBlock; theme: PageTheme }> = ({ block, theme }) => (
  <div
    style={textContentStyle(block, asNewsletterTheme(theme)) as CSSProperties}
    dangerouslySetInnerHTML={{ __html: prepareRichTextHtml(block.html, theme.linkColor, themeHeadingSizes(theme)) }}
  />
);

/** The theme's H1/H2/H3 pixel sizes as the native editor takes them. */
export const headingSizesOf = (theme: PageTheme): [number, number, number] => {
  const s = themeHeadingSizes(theme);
  return [s.h1, s.h2, s.h3];
};

/** Published-fidelity render of a `video` block — a library <video> or an
 *  embedded YouTube <iframe> (responsive 16:9). Shared by the public page and the
 *  editor canvas so edit == publish. `editing` suppresses autoplay on the canvas.
 *  Unmuted autoplay is force-muted (browsers block it otherwise). */
// Responsive 16:9 YouTube/Vimeo embed — shared by the legacy VideoLeaf and the
// File/Folder/Embed block's embed mode so both render identically on edit + publish.
export const EmbedVideoFrame: React.FC<{
  provider: 'youtube' | 'vimeo';
  embedId: string;
  autoplay?: boolean; loop?: boolean; controls?: boolean;
  align?: 'left' | 'center' | 'right'; width?: number; editing?: boolean;
}> = ({ provider, embedId, autoplay, loop, controls = true, align = 'center', width = 100, editing }) => {
  if (!embedId) return null;
  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';
  const w = `${Math.max(10, Math.min(100, width || 100))}%`;
  let src = ''; let allow = ''; let title = '';
  if (provider === 'vimeo') {
    const [vid, vhash] = embedId.split('/'); // `id` or `id/hash` (unlisted)
    const p = new URLSearchParams();
    if (vhash) p.set('h', vhash);
    if (autoplay && !editing) { p.set('autoplay', '1'); p.set('muted', '1'); }
    if (loop) p.set('loop', '1');
    if (!controls) p.set('controls', '0');
    // Vimeo's player defaults to autopause=1, which pauses every OTHER Vimeo
    // player on the page the moment one starts — with several embeds only one
    // ever plays (autoplay or manual). Each embed is an independent block here,
    // so opt out unconditionally.
    p.set('autopause', '0');
    const qs = p.toString();
    src = `https://player.vimeo.com/video/${vid}${qs ? `?${qs}` : ''}`;
    allow = 'autoplay; fullscreen; picture-in-picture; clipboard-write';
    title = 'Vimeo video player';
  } else {
    const p = new URLSearchParams();
    if (autoplay && !editing) { p.set('autoplay', '1'); p.set('mute', '1'); }
    if (loop) { p.set('loop', '1'); p.set('playlist', embedId); }
    if (!controls) p.set('controls', '0');
    const qs = p.toString();
    src = `https://www.youtube.com/embed/${embedId}${qs ? `?${qs}` : ''}`;
    allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    title = 'YouTube video player';
  }
  return (
    <div style={{ display: 'flex', justifyContent: justify, alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
      {/* aspect-ratio (not the padding-bottom trick) reserves the 16:9 height
          DEFINITELY — percentage padding collapses to 0 inside flex columns,
          which let the next block overlap the embed. */}
      <div style={{ width: w, maxWidth: '100%', minWidth: 0, aspectRatio: '16 / 9' }}>
        <iframe src={src} title={title} allow={allow} allowFullScreen
          style={{ width: '100%', height: '100%', border: 0, borderRadius: 8, display: 'block' }} />
      </div>
    </div>
  );
};

// Measure a container's live pixel width (the 3D/pdf/video viewers need real px,
// not a %, to size their canvas). Returns 0 until the first measurement.
function useMeasuredWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => { for (const e of entries) setW(e.contentRect.width); });
    ro.observe(el);
    setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// server/mediaCdn.js's attachSiteMediaVariants() stashes these as sibling
// fields directly on the leaf JSON of PUBLIC page-doc responses when a ready
// lightweight variant exists — intentionally not part of the shared pageBlocks
// data model (shared/pageBlocks.d.ts) since they're a purely additive, may-be-
// absent rendering enhancement, not authored content. The builder canvas
// (EditableCanvas, editing=true) never has these fields on its in-memory
// blocks, so they're simply undefined there — LibraryMediaView already treats
// undefined as "no variant, render the original" everywhere.
type LibraryBlockWithVariants = LibraryBlock & {
  variantPlaceholderSrc?: string;
  variantSrc?: string;
  variantLgSrc?: string;
  videoVariantSrc?: string;
  posterVariantSrc?: string;
};

/** Published-fidelity render of a `library` block. FILE mode renders the single
 *  picked upload via the shared <LibraryMediaView> (3D model / image / video /
 *  pdf / audio — reusing the library-pane viewers). FOLDER mode renders the
 *  public <LibraryFolderBrowser> rooted at the designated shared folder. Shared
 *  by the public page and the editor canvas so edit == publish.
 *  `priority` is threaded down for the page's likely LCP element (see
 *  PageRenderer's top-level section/row/column loop) — ignored everywhere else. */
export const LibraryLeaf: React.FC<{ block: LibraryBlock; editing?: boolean; priority?: boolean; onNavigate?: (t: string) => void }> = ({ block, editing, priority, onNavigate }) => {
  const variants = block as LibraryBlockWithVariants;
  const justify = block.align === 'left' ? 'flex-start' : block.align === 'right' ? 'flex-end' : 'center';
  const widthPct = `${Math.max(10, Math.min(100, block.width || 100))}%`;
  const [ref, measured] = useMeasuredWidth();
  // Outer padding (default 0) wraps every mode.
  const pad: CSSProperties = {
    paddingTop: block.paddingY || 0, paddingBottom: block.paddingY || 0,
    paddingLeft: block.paddingX || 0, paddingRight: block.paddingX || 0,
  };

  // EMBED mode (YouTube/Vimeo) — the shared frame handles its own align/width.
  if (block.mode === 'embed') {
    if (!block.embedId) return null;
    return (
      <div style={pad}>
        <EmbedVideoFrame provider={block.embedProvider || 'youtube'} embedId={block.embedId}
          autoplay={block.autoplay} loop={block.loop} controls={block.controls}
          align={block.align} width={block.width} editing={editing} />
      </div>
    );
  }

  if (block.mode === 'folder') {
    if (!block.folderId) return null;
    return (
      <div style={pad}>
        <div style={{ display: 'flex', justifyContent: justify }}>
          <div ref={ref} style={{ width: widthPct, maxWidth: '100%' }}>
            <LibraryFolderBrowser block={block} editing={editing} />
          </div>
        </div>
      </div>
    );
  }

  // FILE mode.
  if (!block.src) return null;
  const kind = getPreviewKind(block.mime || '', block.filename || '');
  // Image renders at natural height; the framed viewers (3D/pdf/video) get a
  // height derived from the measured column width.
  const boxW = measured || 0;
  const height = Math.round(Math.min(560, Math.max(280, (boxW || 360) * 0.62)));
  // href/caption/alt/sectionColor are image-only, absorbed from the legacy
  // standalone image block (see imageLeafToLibrary in shared/pageBlocks.js)
  // — inert for every other kind, matching the old block's own image-only
  // scope.
  const media = (
    <LibraryMediaView
      src={block.src}
      filename={block.filename}
      alt={block.alt}
      mime={block.mime}
      width={boxW || 360}
      height={height}
      autoRotate={block.autoRotate}
      bgColor={block.bgColor}
      videoControls={block.controls !== false}
      videoAutoPlay={!!block.autoplay && !editing}
      videoLoop={block.loop}
      videoMuted={block.muted}
      variantPlaceholderSrc={variants.variantPlaceholderSrc}
      variantSrc={variants.variantSrc}
      variantLgSrc={variants.variantLgSrc}
      videoVariantSrc={variants.videoVariantSrc}
      posterVariantSrc={variants.posterVariantSrc}
      priority={priority}
    />
  );
  // sectionColor is image-only (legacy imageBlockStyle parity) — applied here,
  // not merged into the shared `pad` above, so it can never bleed into
  // folder/embed mode's background.
  const filePad: CSSProperties = kind === 'image' && block.sectionColor && block.sectionColor !== 'transparent'
    ? { ...pad, backgroundColor: block.sectionColor }
    : pad;
  return (
    <div style={filePad}>
      <div style={{ display: 'flex', justifyContent: justify }}>
        <div ref={ref} style={{ width: widthPct, maxWidth: '100%' }}>
          {(boxW > 0 || kind === 'image' || kind === 'audio') && (
            kind === 'image' && block.href ? <SmartLink target={block.href} onNavigate={onNavigate}>{media}</SmartLink> : media
          )}
          {kind === 'image' && block.showCaption && block.caption && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#71717a' }}>{block.caption}</p>
          )}
        </div>
      </div>
    </div>
  );
};

/** Published-fidelity render of a `mini-nav` block. Shared by the public page
 *  and the editor canvas so edit == publish; the canvas wraps it in its own
 *  pointer-events-disabling shell rather than suppressing navigation here. */
export const MiniNavLeaf: React.FC<{ block: MiniNavBlock; onNavigate?: (t: string) => void }> = ({ block, onNavigate }) => {
  const pad: CSSProperties = {
    paddingTop: block.paddingY || 0, paddingBottom: block.paddingY || 0,
    paddingLeft: block.paddingX || 0, paddingRight: block.paddingX || 0,
  };
  return (
    <nav style={{ ...pad, display: 'flex', flexWrap: 'wrap', gap: block.gap, justifyContent: block.align === 'left' ? 'flex-start' : block.align === 'right' ? 'flex-end' : 'center' }}>
      {block.links.map((l) => (
        <SmartLink key={l.id} target={l.target} onNavigate={onNavigate} style={{
          color: l.color || block.color,
          fontSize: l.fontSize ?? block.fontSize,
          fontWeight: l.weight,
          textDecoration: l.underline ? 'underline' : 'none',
        }}>{l.label}</SmartLink>
      ))}
    </nav>
  );
};

/** A hero's static heading + subheading (rich HTML). Empty fields are omitted,
 *  matching the live page. The CTA differs by context (public = navigating link,
 *  editor = inert span), so the caller renders it with `heroCtaStyle`. */
export const HeroRichText: React.FC<{ hero: HeroBlock; theme: PageTheme }> = ({ hero, theme }) => (
  <>
    {hero.heading && (
      <div style={heroHeadingStyle(theme)} dangerouslySetInnerHTML={{ __html: prepareRichTextHtml(hero.heading, theme.linkColor) }} />
    )}
    {hero.subheading && (
      <div style={heroSubStyle} dangerouslySetInnerHTML={{ __html: prepareRichTextHtml(hero.subheading, theme.linkColor) }} />
    )}
  </>
);
